import { useCallback, useEffect, useState } from "react";

import CustomSelectView from "./CustomSelectView";
import { ICustomSelectProps, IOptionSelect } from "@/types/customSelectTypes";

import { useTheme } from "@/providers/ThemeProvider";

/**
 * Container component for custom select dropdown with theme support
 * @param root0 - The component props
 * @param root0.options - Array of selectable options to display in dropdown
 * @param root0.placeholder - Text to show when no option is selected
 * @param root0.onChangeValue - Callback invoked when user selects a different option with the new value
 * @param root0.defaultValue - Initial selected option value
 * @param root0.isUpdated - Whether to reset selection when defaultValue changes
 */
const CustomSelectContainer = ({
  options,
  placeholder,
  onChangeValue,
  defaultValue,
  isUpdated = false,
}: ICustomSelectProps) => {
  const [selectedOption, setSelectedOption] = useState<
    IOptionSelect | undefined
  >(undefined);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const handleChangeSelect = useCallback(
    (option: IOptionSelect) => {
      setSelectedOption(option);
      onChangeValue(option.value);
    },
    [onChangeValue]
  );

  useEffect(() => {
    if (defaultValue && (!selectedOption || isUpdated)) {
      const findValue = options.find(option => option.value === defaultValue);
      if (findValue) {
        // eslint-disable-next-line react-hooks/set-state-in-effect -- ANTI-PATTERN: Syncing state with prop. FIX: Make component fully controlled or derive selectedOption with useMemo from defaultValue prop
        setSelectedOption(findValue);
      }
    }
  }, [defaultValue, isUpdated, options, selectedOption]);

  return (
    <CustomSelectView
      options={options}
      selected={selectedOption}
      defaultValue={defaultValue}
      placeholder={placeholder}
      isDark={isDark}
      onChangeSelect={handleChangeSelect}
      onChangeSelectSystem={onChangeValue}
    />
  );
};

export default CustomSelectContainer;
